// src/components/IncomingCall/IncomingCallNotification.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRingingDuration,
  resolveCallControlChannelId,
  resolveIncomingCallDisplayId
} from '../../utils/incomingCallNotification';
import { ASTERISK_API_BASE } from '../../config/bridge';

const RINGTONE_OUTPUT_DEVICE_ID_KEY = 'frontOfficeNew_ringtoneOutputDeviceId';
const RINGTONE_OUTPUT_CHANGED_EVENT = 'frontOfficeNew:ringtone-output-changed';
const LOUDSPEAKER_LABEL_PATTERN = /\b(speakers?|built[- ]?in output)\b/i;

const getIncomingCallKey = (call = {}) =>
  String(call?.channelId || call?.stableId || call?.callerNumber || '').trim();

const getIncomingCallerNumber = (call = {}) =>
  String(call?.callerNumber ?? call?.cli ?? call?.number ?? '').trim();

const getResolvedCallId = (payload = {}) =>
  String(
    payload.callId ||
      payload.callStateUniqueId ||
      payload.uniqueId ||
      payload.UniqueId ||
      ''
  ).trim();

const getTransferredByLabel = (call = {}) => {
  const name = String(call.transferredByName || '').trim();
  const extension = String(call.transferredByExtension || '').trim();
  if (name && extension && name !== extension) return `${name} (${extension})`;
  return name || extension;
};

const IncomingCallNotification = ({
  onAcceptCall,
  onAcceptStart,
  agentId,
  isAgentUnavailable = false,
  incomingCalls: incomingCallsProp,
  acceptIncomingCall,
  rejectIncomingCall,
  acceptSipIncomingCall,
  rejectSipIncomingCall,
  cancelPendingAutoAnswer,
  onHangup,
  isCallActive = false,
  isConnected = true,
  inline = false
}) => {
  const areIncomingCallListsEqual = useCallback((prevCalls, nextCalls) => {
    if (prevCalls.length !== nextCalls.length) {
      return false;
    }

    return prevCalls.every((call, idx) => {
      const nextCall = nextCalls[idx];
      return String(call?.channelId || '') === String(nextCall?.channelId || '') &&
        String(call?.callerName || '') === String(nextCall?.callerName || '') &&
        String(call?.callerNumber || '') === String(nextCall?.callerNumber || '') &&
        String(resolveIncomingCallDisplayId(call)) === String(resolveIncomingCallDisplayId(nextCall)) &&
        String(call?.stableId || '') === String(nextCall?.stableId || '') &&
        String(call?.linkedId || '') === String(nextCall?.linkedId || '') &&
        String(call?.ivrUid || '') === String(nextCall?.ivrUid || '') &&
        String(call?.callStateChannelId || '') === String(nextCall?.callStateChannelId || '') &&
        String(call?.ariChannelId || '') === String(nextCall?.ariChannelId || '') &&
        String(call?.queueName || '') === String(nextCall?.queueName || '') &&
        String(call?.languageCode || '') === String(nextCall?.languageCode || '') &&
        String(call?.languageName || '') === String(nextCall?.languageName || '') &&
        String(call?.transferredByExtension || '') === String(nextCall?.transferredByExtension || '') &&
        String(call?.transferredByName || '') === String(nextCall?.transferredByName || '') &&
        Boolean(call?.transferMetadataResolved) === Boolean(nextCall?.transferMetadataResolved) &&
        String(call?.receivedAt || '') === String(nextCall?.receivedAt || '') &&
        Number(call?.duration || 0) === Number(nextCall?.duration || 0);
    });
  }, []);

  const effectiveIncomingCalls = useMemo(
    () => (Array.isArray(incomingCallsProp) ? incomingCallsProp.filter(Boolean) : []),
    [incomingCallsProp]
  );
  const hasRenderableIncomingCall = useCallback((call) =>
    Boolean(String(call?.callerNumber ?? call?.cli ?? call?.number ?? '').trim()), []);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [acceptingCallIds, setAcceptingCallIds] = useState([]);
  const [dismissedCallIds, setDismissedCallIds] = useState([]);
  const [acceptedInlineCall, setAcceptedInlineCall] = useState(null);
  const [resolvedCallIds, setResolvedCallIds] = useState({});
  const [resolvedChannelIds, setResolvedChannelIds] = useState({});
  const [resolvedCallMetadata, setResolvedCallMetadata] = useState({});
  const [isRingtoneBlocked, setIsRingtoneBlocked] = useState(false);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const ringAudioRef = useRef(null);
  const ringtoneUnlockedRef = useRef(false);
  const callIdLookupAttemptedRef = useRef(new Set());
  const callIdLookupRetryTimersRef = useRef(new Map());
  const shouldShowModalRef = useRef(false);
  const offlineBannerRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0
  });
  const [hasMeasuredBanner, setHasMeasuredBanner] = useState(false);
  const [offlineBannerPosition, setOfflineBannerPosition] = useState(() => {
    if (typeof window === 'undefined') {
      return { x: 16, y: 16 };
    }
    return { x: Math.max(16, window.innerWidth - 360), y: 16 };
  });

  const enrichedEffectiveIncomingCalls = useMemo(
    () => effectiveIncomingCalls.map((call) => {
      const lookupKey = getIncomingCallKey(call);
      const resolvedId = lookupKey ? resolvedCallIds[lookupKey] : '';
      const resolvedChannelId = lookupKey ? resolvedChannelIds[lookupKey] : '';
      const resolvedMetadata = lookupKey ? resolvedCallMetadata[lookupKey] : null;
      if (!resolvedId && !resolvedChannelId && !resolvedMetadata) return call;

      return {
        ...call,
        callerNumber: resolvedMetadata?.callerNumber || call.callerNumber,
        callId: call.callId || resolvedId,
        uniqueId: call.uniqueId || resolvedId,
        webCallId: call.webCallId || resolvedId,
        callStateUniqueId: call.callStateUniqueId || resolvedId,
        callStateChannelId: call.callStateChannelId || resolvedChannelId,
        ariChannelId: call.ariChannelId || resolvedChannelId,
        queueName: call.queueName || resolvedMetadata?.queueName || '',
        languageCode: call.languageCode || resolvedMetadata?.languageCode || '',
        languageName: call.languageName || resolvedMetadata?.languageName || '',
        transferredByExtension:
          call.transferredByExtension || resolvedMetadata?.transferredByExtension || '',
        transferredByName: call.transferredByName || resolvedMetadata?.transferredByName || '',
        transferMetadataResolved:
          Boolean(call.transferMetadataResolved) || Boolean(resolvedMetadata?.transferMetadataResolved)
      };
    }),
    [effectiveIncomingCalls, resolvedCallIds, resolvedCallMetadata, resolvedChannelIds]
  );

  const activeIncomingCallIds = useMemo(
    () => new Set(enrichedEffectiveIncomingCalls.map((call) => call.channelId)),
    [enrichedEffectiveIncomingCalls]
  );

  const scheduleCallIdLookupRetry = useCallback((lookupKey) => {
    if (!lookupKey || callIdLookupRetryTimersRef.current.has(lookupKey)) return;

    const timer = setTimeout(() => {
      callIdLookupRetryTimersRef.current.delete(lookupKey);
      callIdLookupAttemptedRef.current.delete(lookupKey);
      setResolvedCallIds((prev) => ({ ...prev }));
    }, 1000);
    callIdLookupRetryTimersRef.current.set(lookupKey, timer);
  }, []);

  useEffect(() => () => {
    callIdLookupRetryTimersRef.current.forEach((timer) => clearTimeout(timer));
    callIdLookupRetryTimersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!isConnected || typeof fetch !== 'function') return undefined;

    const controller = new AbortController();
    enrichedEffectiveIncomingCalls.forEach((call) => {
      if (
        !call ||
        call.source !== 'sip' ||
        (
          resolveIncomingCallDisplayId(call) &&
          resolveCallControlChannelId(call) &&
          call.queueName &&
          call.languageName &&
          call.transferMetadataResolved
        )
      ) return;

      const lookupKey = getIncomingCallKey(call);
      const callerNumber = getIncomingCallerNumber(call);
      if (!lookupKey || !callerNumber || callIdLookupAttemptedRef.current.has(lookupKey)) return;

      callIdLookupAttemptedRef.current.add(lookupKey);
      const params = new URLSearchParams();
      params.set('callerNumber', callerNumber);
      if (call.channelId && !String(call.channelId).startsWith('sip:')) {
        params.set('channelId', call.channelId);
      }
      if (agentId) params.set('agentId', agentId);

      fetch(`${ASTERISK_API_BASE}/call-context?${params.toString()}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((payload) => {
          const resolvedId = getResolvedCallId(payload || {});
          const resolvedChannelId = resolveCallControlChannelId(payload || {});
          const queueName = String(payload?.queueName || '').trim();
          const languageCode = String(payload?.languageCode || '').trim();
          const languageName = String(payload?.languageName || '').trim();
          const resolvedCallerNumber = String(payload?.callerNumber || '').trim();
          const transferredByExtension = String(payload?.transferredByExtension || '').trim();
          const transferredByName = String(payload?.transferredByName || '').trim();
          const transferMetadataResolved = payload?.transferMetadataResolved === true;
          const hasQueueMetadata = Boolean(queueName || languageName);
          const hasResolvedMetadata = hasQueueMetadata || transferMetadataResolved;
          if (!resolvedId && !resolvedChannelId && !hasResolvedMetadata) {
            scheduleCallIdLookupRetry(lookupKey);
            return;
          }
          const retryTimer = callIdLookupRetryTimersRef.current.get(lookupKey);
          if (retryTimer) {
            clearTimeout(retryTimer);
            callIdLookupRetryTimersRef.current.delete(lookupKey);
          }
          if (resolvedId) {
            setResolvedCallIds((prev) => (
              prev[lookupKey] === resolvedId ? prev : { ...prev, [lookupKey]: resolvedId }
            ));
          }
          if (resolvedChannelId) {
            setResolvedChannelIds((prev) => (
              prev[lookupKey] === resolvedChannelId
                ? prev
                : { ...prev, [lookupKey]: resolvedChannelId }
            ));
          }
          if (hasResolvedMetadata) {
            setResolvedCallMetadata((prev) => {
              const current = prev[lookupKey] || {};
              if (
                current.queueName === queueName &&
                current.languageCode === languageCode &&
                current.languageName === languageName &&
                current.callerNumber === resolvedCallerNumber &&
                current.transferredByExtension === transferredByExtension &&
                current.transferredByName === transferredByName &&
                current.transferMetadataResolved === transferMetadataResolved
              ) return prev;
              return {
                ...prev,
                [lookupKey]: {
                  queueName,
                  languageCode,
                  languageName,
                  callerNumber: resolvedCallerNumber,
                  transferredByExtension,
                  transferredByName,
                  transferMetadataResolved
                }
              };
            });
          } else {
            scheduleCallIdLookupRetry(lookupKey);
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            callIdLookupAttemptedRef.current.delete(lookupKey);
            scheduleCallIdLookupRetry(lookupKey);
          }
        });
    });

    return () => controller.abort();
  }, [agentId, enrichedEffectiveIncomingCalls, isConnected, scheduleCallIdLookupRetry]);

  useEffect(() => {
    const activeKeys = new Set(enrichedEffectiveIncomingCalls.map(getIncomingCallKey).filter(Boolean));
    setResolvedCallIds((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (activeKeys.has(key)) next[key] = value;
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
    setResolvedChannelIds((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (activeKeys.has(key)) next[key] = value;
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
    setResolvedCallMetadata((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (activeKeys.has(key)) next[key] = value;
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [enrichedEffectiveIncomingCalls]);

  useEffect(() => {
    if (isAgentUnavailable) {
      setIncomingCalls([]);
      setIsVisible(false);
      if (acceptingCallIds.length) {
        setAcceptingCallIds([]);
      }
      if (dismissedCallIds.length) {
        setDismissedCallIds([]);
      }
      return;
    }

    const filtered = enrichedEffectiveIncomingCalls.filter(
      (call) =>
        hasRenderableIncomingCall(call) &&
        !acceptingCallIds.includes(call.channelId) &&
        !dismissedCallIds.includes(call.channelId)
    );

    setIncomingCalls((prev) => {
      if (areIncomingCallListsEqual(prev, filtered)) {
        return prev;
      }
      return filtered;
    });
    setIsVisible((prev) => (prev === (filtered.length > 0) ? prev : filtered.length > 0));
  }, [acceptingCallIds, areIncomingCallListsEqual, dismissedCallIds, enrichedEffectiveIncomingCalls, hasRenderableIncomingCall, isAgentUnavailable]);

  useEffect(() => {
    if (!acceptingCallIds.length) return;
    const next = acceptingCallIds.filter((id) => activeIncomingCallIds.has(id));
    if (next.length !== acceptingCallIds.length) {
      setAcceptingCallIds(next);
    }
  }, [acceptingCallIds, activeIncomingCallIds]);

  useEffect(() => {
    if (!dismissedCallIds.length) return;
    const next = dismissedCallIds.filter((id) => activeIncomingCallIds.has(id));
    if (next.length !== dismissedCallIds.length) {
      setDismissedCallIds(next);
    }
  }, [activeIncomingCallIds, dismissedCallIds]);

  const shouldShowModal = isVisible && incomingCalls.length > 0;
  const inlineCalls = useMemo(() => {
    if (incomingCalls.length > 0) return incomingCalls;
    return acceptedInlineCall && isCallActive ? [acceptedInlineCall] : [];
  }, [acceptedInlineCall, incomingCalls, isCallActive]);
  const shouldShowInlinePanel = inline && inlineCalls.length > 0;
  const hasVisibleSipCall = incomingCalls.some((call) => call?.source === 'sip');
  const ringToneSrc = `${process.env.PUBLIC_URL}/RingTone/Ring.mp3`;

  useEffect(() => {
    if (!shouldShowModal || !hasVisibleSipCall) {
      return undefined;
    }

    setClockNow(Date.now());
    const timer = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasVisibleSipCall, shouldShowModal]);

  useEffect(() => {
    shouldShowModalRef.current = shouldShowModal;
  }, [shouldShowModal]);

  const stopRingtone = useCallback(() => {
    const audio = ringAudioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const applyRingtoneOutput = useCallback(async () => {
    const audio = ringAudioRef.current;
    if (!audio || typeof audio.setSinkId !== 'function') return false;

    try {
      const storedDeviceId = localStorage.getItem(RINGTONE_OUTPUT_DEVICE_ID_KEY) || '';
      let deviceId = storedDeviceId;
      if (typeof navigator.mediaDevices?.enumerateDevices === 'function') {
        try {
          const outputs = (await navigator.mediaDevices.enumerateDevices())
            .filter((device) => device.kind === 'audiooutput');
          const storedDevice = outputs.find((device) => device.deviceId === storedDeviceId);
          const loudspeaker = outputs.find((device) => LOUDSPEAKER_LABEL_PATTERN.test(device.label || ''));
          deviceId = storedDevice?.deviceId || loudspeaker?.deviceId || storedDeviceId;
        } catch {
          // The saved output can still be valid when device enumeration is unavailable.
        }
      }
      if (!deviceId) return false;
      await audio.setSinkId(deviceId);
      return true;
    } catch (error) {
      console.warn('Ringtone speaker selection failed:', error);
      return false;
    }
  }, []);

  const attemptRingtonePlayback = useCallback(async (reason = '') => {
    const audio = ringAudioRef.current;
    if (!audio) return false;

    try {
      await applyRingtoneOutput();
      audio.muted = false;
      audio.volume = 1;
      const playAttempt = audio.play();
      if (playAttempt && typeof playAttempt.then === 'function') {
        await playAttempt;
      }
      ringtoneUnlockedRef.current = true;
      setIsRingtoneBlocked(false);
      return true;
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        setIsRingtoneBlocked(true);
      } else {
        console.warn(`Ring tone playback failed${reason ? ` (${reason})` : ''}:`, error);
      }
      return false;
    }
  }, [applyRingtoneOutput]);

  const unlockRingtonePlayback = useCallback(async () => {
    const audio = ringAudioRef.current;
    if (!audio || ringtoneUnlockedRef.current) return false;

    const previousMuted = audio.muted;
    const previousVolume = audio.volume;

    try {
      audio.muted = true;
      audio.volume = 0;
      const playAttempt = audio.play();
      if (playAttempt && typeof playAttempt.then === 'function') {
        await playAttempt;
      }
      audio.pause();
      audio.currentTime = 0;
      audio.muted = previousMuted;
      audio.volume = previousVolume;
      ringtoneUnlockedRef.current = true;
      setIsRingtoneBlocked(false);

      if (shouldShowModalRef.current) {
        await attemptRingtonePlayback('post-unlock');
      }
      return true;
    } catch (error) {
      audio.muted = previousMuted;
      audio.volume = previousVolume;
      return false;
    }
  }, [attemptRingtonePlayback]);

  useEffect(() => {
    const audio = ringAudioRef.current;
    if (!audio) return undefined;

    audio.load();
    applyRingtoneOutput().catch(() => {});

    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleUserInteraction = () => {
      unlockRingtonePlayback().catch(() => {});
    };
    const handleRingtoneOutputChanged = () => {
      applyRingtoneOutput().catch(() => {});
    };

    const interactionEvents = ['pointerdown', 'keydown', 'touchstart'];
    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserInteraction, true);
    });
    window.addEventListener(RINGTONE_OUTPUT_CHANGED_EVENT, handleRingtoneOutputChanged);
    navigator.mediaDevices?.addEventListener?.('devicechange', handleRingtoneOutputChanged);

    return () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserInteraction, true);
      });
      window.removeEventListener(RINGTONE_OUTPUT_CHANGED_EVENT, handleRingtoneOutputChanged);
      navigator.mediaDevices?.removeEventListener?.('devicechange', handleRingtoneOutputChanged);
    };
  }, [applyRingtoneOutput, unlockRingtonePlayback]);

  useEffect(() => {
    if (shouldShowModal) {
      attemptRingtonePlayback('incoming-call').catch(() => {});
      return;
    }

    setIsRingtoneBlocked(false);
    stopRingtone();
  }, [attemptRingtonePlayback, shouldShowModal, stopRingtone]);

  useEffect(() => {
    return () => {
      stopRingtone();
    };
  }, [stopRingtone]);

  useEffect(() => {
    if (hasMeasuredBanner || isConnected || !offlineBannerRef.current) {
      return;
    }
    const rect = offlineBannerRef.current.getBoundingClientRect();
    const offset = 16;
    const nextX = Math.max(offset, window.innerWidth - rect.width - offset);
    const nextY = offset;
    setOfflineBannerPosition((prev) => {
      if (prev.x === nextX && prev.y === nextY) {
        return prev;
      }
      return { x: nextX, y: nextY };
    });
    setHasMeasuredBanner(true);
  }, [hasMeasuredBanner, isConnected]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current.isDragging || !offlineBannerRef.current) {
        return;
      }

      const rect = offlineBannerRef.current.getBoundingClientRect();
      const maxX = Math.max(0, window.innerWidth - rect.width);
      const maxY = Math.max(0, window.innerHeight - rect.height);
      const deltaX = event.clientX - dragStateRef.current.startX;
      const deltaY = event.clientY - dragStateRef.current.startY;
      const nextX = Math.min(
        Math.max(dragStateRef.current.originX + deltaX, 0),
        maxX
      );
      const nextY = Math.min(
        Math.max(dragStateRef.current.originY + deltaY, 0),
        maxY
      );

      setOfflineBannerPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = () => {
      dragStateRef.current.isDragging = false;
    };

    const handleResize = () => {
      if (!offlineBannerRef.current) {
        return;
      }
      const rect = offlineBannerRef.current.getBoundingClientRect();
      const maxX = Math.max(0, window.innerWidth - rect.width);
      const maxY = Math.max(0, window.innerHeight - rect.height);
      setOfflineBannerPosition((prev) => ({
        x: Math.min(Math.max(prev.x, 0), maxX),
        y: Math.min(Math.max(prev.y, 0), maxY)
      }));
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleOfflineBannerPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    event.preventDefault();
    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: offlineBannerPosition.x,
      originY: offlineBannerPosition.y
    };
  }, [offlineBannerPosition.x, offlineBannerPosition.y]);

  const handleAccept = useCallback(async (call) => {
    const isSipCall = call?.source === 'sip';

    try {
      if (isSipCall) {
        if (!acceptSipIncomingCall) {
          throw new Error('SIP accept handler not available');
        }
      } else if (!acceptIncomingCall) {
        throw new Error('Accept call handler not available');
      }

      stopRingtone();

      setAcceptingCallIds((prev) =>
        prev.includes(call.channelId) ? prev : [...prev, call.channelId]
      );

      if (!isSipCall && onAcceptStart) {
        const autoAnswerReady = await onAcceptStart(call);
        if (autoAnswerReady === false) {
          throw new Error('Microphone access is required before accepting the call');
        }
      }

      const result = isSipCall
        ? await acceptSipIncomingCall(call)
        : await acceptIncomingCall(call.channelId, agentId);

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to accept call');
      }

      if (inline) {
        setAcceptedInlineCall(result.callInfo || call);
      }

      if (!isSipCall && onAcceptCall) {
        onAcceptCall(result.callInfo || call);
      }
    } catch (error) {
      console.error('Accept call failed:', error);
      if (cancelPendingAutoAnswer) {
        cancelPendingAutoAnswer(true);
      }
      setAcceptingCallIds((prev) => prev.filter((id) => id !== call.channelId));
      alert(`Error accepting call: ${error.message || error}`);
    }
  }, [acceptIncomingCall, acceptSipIncomingCall, agentId, cancelPendingAutoAnswer, inline, onAcceptCall, onAcceptStart, stopRingtone]);

  const handleInlineHangup = useCallback(async () => {
    if (!onHangup) return;
    await Promise.resolve(onHangup());
  }, [onHangup]);

  const handleReject = useCallback(async (call) => {
    const isSipCall = call?.source === 'sip';

    try {
      stopRingtone();
      setDismissedCallIds((prev) =>
        prev.includes(call.channelId) ? prev : [...prev, call.channelId]
      );

      if (isSipCall) {
        if (rejectSipIncomingCall) {
          rejectSipIncomingCall();
        }
        return;
      }

      if (!rejectIncomingCall) {
        throw new Error('Reject call handler not available');
      }

      const result = await rejectIncomingCall(call.channelId, agentId);
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to reject call');
      }
    } catch (error) {
      console.error('Reject call failed:', error);
      setDismissedCallIds((prev) => prev.filter((id) => id !== call.channelId));
      alert(`Error rejecting call: ${error.message || error}`);
    }
  }, [agentId, rejectIncomingCall, rejectSipIncomingCall, stopRingtone]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const OfflineBanner = () => (
    <div
      className="position-fixed p-3"
      style={{ zIndex: 1200, top: `${offlineBannerPosition.y}px`, left: `${offlineBannerPosition.x}px` }}
    >
      <div
        ref={offlineBannerRef}
        className="toast show text-bg-warning border-0 shadow"
        onPointerDown={handleOfflineBannerPointerDown}
        style={{ cursor: 'move', userSelect: 'none', touchAction: 'none' }}
        role="status"
        aria-live="polite"
      >
        <div className="toast-body d-flex align-items-center gap-2">
          <i className="bi bi-wifi-off"></i>
          <span>ARI bridge disconnected. Reconnecting...</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!isConnected && <OfflineBanner />}
      <audio ref={ringAudioRef} src={ringToneSrc} preload="auto" loop className="d-none" />

      {shouldShowInlinePanel && (
        <div
          className="border border-dark rounded-2 bg-white p-2 mb-3"
          role="status"
          aria-live="assertive"
        >
          {inlineCalls.map((call, index) => {
            const isCurrentIncomingCall = incomingCalls.some(
              (incomingCall) => getIncomingCallKey(incomingCall) === getIncomingCallKey(call)
            );
            const isStoredCall = !isCurrentIncomingCall && Boolean(
              acceptedInlineCall &&
              getIncomingCallKey(call) === getIncomingCallKey(acceptedInlineCall)
            );
            const isAcceptedCall = isStoredCall && isCallActive;
            const isReadOnlyCall = isStoredCall && !isCallActive;

            return (
            <div
              key={call.channelId}
              className={`d-flex align-items-center gap-2 ${index > 0 ? 'border-top pt-2 mt-2' : ''}`}
            >
              <div
                className="bg-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: '34px', height: '34px' }}
              >
                <i className="bi bi-person text-white" style={{ fontSize: '1rem' }}></i>
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-1 fw-bold text-dark small">
                  <i className="bi bi-telephone-inbound-fill text-danger"></i>
                  <span>Incoming Call</span>
                </div>
                <div className="fw-semibold" style={{ fontSize: '0.7rem' }}>
                  {call.callerName || 'Unknown Caller'}
                </div>
                <div className="text-primary fw-semibold" style={{ fontSize: '0.68rem' }}>
                  {call.callerNumber || 'Private Number'}
                </div>
                {resolveIncomingCallDisplayId(call) && (
                  <div className="text-muted text-break" style={{ fontSize: '0.62rem' }}>
                    Call ID: {resolveIncomingCallDisplayId(call)}
                  </div>
                )}
                {getTransferredByLabel(call) && (
                  <div className="text-info-emphasis fw-semibold text-break" style={{ fontSize: '0.62rem' }}>
                    Transferred by: {getTransferredByLabel(call)}
                  </div>
                )}
                {(call.languageName || call.queueName) && (
                  <div className="text-success fw-semibold text-break" style={{ fontSize: '0.62rem' }}>
                    {call.languageName && `Language: ${call.languageName}`}
                    {call.languageName && call.queueName && ' | '}
                    {call.queueName && `Queue: ${call.queueName}`}
                  </div>
                )}
                <div className="text-muted" style={{ fontSize: '0.62rem' }}>
                  <i className={`bi ${isAcceptedCall ? 'bi-telephone-fill' : 'bi-clock'} me-1`}></i>
                  {isAcceptedCall
                    ? 'On call'
                    : isReadOnlyCall
                      ? 'Call ended'
                    : `Ringing for ${formatDuration(getRingingDuration(call, clockNow))}`}
                </div>
              </div>
              {!isReadOnlyCall && (
                <div className="d-flex flex-column gap-1 flex-shrink-0" style={{ width: '72px' }}>
                <button
                  className={`btn ${isAcceptedCall ? 'btn-danger' : 'btn-success'} btn-sm d-flex align-items-center justify-content-center gap-1`}
                  onClick={isAcceptedCall ? handleInlineHangup : () => handleAccept(call)}
                  style={{ fontSize: '0.62rem' }}
                >
                  <i className={`bi ${isAcceptedCall ? 'bi-telephone-x' : 'bi-telephone-plus'}`}></i>
                  {isAcceptedCall ? 'Hangup' : 'Accept'}
                </button>
                {!isAcceptedCall && (
                  <button
                    className="btn btn-danger btn-sm d-flex align-items-center justify-content-center gap-1"
                    onClick={() => handleReject(call)}
                    style={{ fontSize: '0.62rem' }}
                  >
                    <i className="bi bi-telephone-x"></i>
                    Reject
                  </button>
                )}
                </div>
              )}
            </div>
            );
          })}
          {isRingtoneBlocked && (
            <div className="small text-warning mt-2">
              Click anywhere once to enable ringtone alerts.
            </div>
          )}
        </div>
      )}

      {shouldShowModal && !inline && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 2000 }}
        >
          <div className="bg-white rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', width: '90%' }}>
            <div className="text-center mb-4">
              <div
                className="bg-success rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px' }}
              >
                <i className="bi bi-telephone-inbound text-white" style={{ fontSize: '2rem' }}></i>
              </div>
              <h4 className="fw-bold text-dark mb-2">Incoming Call</h4>
              <p className="text-muted small mb-0">A caller is trying to reach you</p>
            </div>

            {incomingCalls.map((call) => (
              <div key={call.channelId} className="border rounded-3 p-3 mb-3 bg-light">
                <div className="row align-items-center">
                  <div className="col-8">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                      >
                        <i className="bi bi-person text-white" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">{call.callerName || 'Unknown Caller'}</h6>
                        <p className="mb-1 text-primary fw-semibold">{call.callerNumber || 'Private Number'}</p>
                        {resolveIncomingCallDisplayId(call) && (
                          <small className="text-muted d-block text-break">
                            Call ID: {resolveIncomingCallDisplayId(call)}
                          </small>
                        )}
                        {getTransferredByLabel(call) && (
                          <small className="text-info-emphasis fw-semibold d-block text-break">
                            Transferred by: {getTransferredByLabel(call)}
                          </small>
                        )}
                        {(call.languageName || call.queueName) && (
                          <small className="text-success fw-semibold d-block text-break">
                            {call.languageName && `Language: ${call.languageName}`}
                            {call.languageName && call.queueName && ' | '}
                            {call.queueName && `Queue: ${call.queueName}`}
                          </small>
                        )}
                        <small className="text-muted">
                          <i className="bi bi-clock me-1"></i>
                          Ringing for {formatDuration(getRingingDuration(call, clockNow))}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="d-flex flex-column gap-2">
                      <button
                        className="btn btn-success btn-sm d-flex align-items-center justify-content-center gap-2"
                        onClick={() => handleAccept(call)}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <i className="bi bi-telephone-plus"></i>
                        Accept
                      </button>
                      <button
                        className="btn btn-danger btn-sm d-flex align-items-center justify-content-center gap-2"
                        onClick={() => handleReject(call)}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <i className="bi bi-telephone-x"></i>
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="text-center mt-3">
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Answer the call to start conversation
              </small>
              {isRingtoneBlocked && (
                <div className="small text-warning mt-2">
                  Click anywhere once to enable ringtone alerts.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IncomingCallNotification;
